import { Layout } from "antd";
import { useEffect, ReactNode } from "react";
import { Helmet } from "react-helmet";
import { Button, Result } from "antd";
import styled from "styled-components";
import { useNetworkState } from "react-use";

import { openNotificationWithIcon } from "utils";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { updatelastIsRejectedApiError } from "app/configSlice";

interface ErrorNotificationProps {
  children: ReactNode;
}

const ErrorNotification = ({ children }: ErrorNotificationProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const networkState = useNetworkState();

  const appConfig = useAppSelector((state) => state.configuration);

  const errorMeta = appConfig?.lastIsRejectedApiError?.meta;
  const errorData = appConfig?.lastIsRejectedApiError?.payload;
  const errorStatus = appConfig?.lastIsRejectedApiError?.payload?.status;


  useEffect(() => {
    return () => {
      dispatch(updatelastIsRejectedApiError({}));
    };
  }, [dispatch]);

  if (networkState.online && errorStatus === 401) {
    const description =
      errorData?.data?.error === "invalid_token"
        ? "your token is either invalid or has expired."
        : "Sorry, the page you visited does not exist.";
    openNotificationWithIcon(errorStatus, description, "error", errorMeta?.requestId);
  }

  if (networkState.online && errorStatus === "PARSING_ERROR" && errorData?.originalStatus !== 500) {
    const description = `${
      errorData && errorData?.data?.toLowerCase().includes("proxy error")
        ? "Proxy Error: Unable to load, check if your proxy or VPN is well connected and refresh page"
        : `${errorData?.error || "Parsing error"}`
    }`;
    openNotificationWithIcon(errorStatus, description, "error", errorMeta?.requestId);
  }

  if (networkState.online && errorStatus === "PARSING_ERROR" && errorData?.originalStatus === 500) {
    const description = "HTTP Status 500 – Internal Server Error ";
    openNotificationWithIcon(errorStatus, description, "error", errorMeta?.requestId);
  }

  if (networkState.online && errorStatus === 404) {
    return (
      <Result
        status="404"
        title={errorStatus}
        subTitle="Sorry, the page you visited does not exist."
        extra={<Button type="primary">Back Home</Button>}
      />
    );
  }

  if (networkState.online && errorStatus === 500) {
    const description = "HTTP Status 500 – Internal Server Error ";
    openNotificationWithIcon(errorStatus, description, "error", errorMeta?.requestId);
  }

  return <>{children}</>;
};

const ErrorHandlerContainer = styled(Layout)`
  .ant-result svg {
    font-size: 50px;
  }
`;

interface ErrorHandlerProps {
  children: ReactNode;
}

const ErrorHandler = ({ children }: ErrorHandlerProps): JSX.Element => {
  return (
    <ErrorHandlerContainer>
      <Helmet>
        <title>NEFT Settlement | Error Page</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <ErrorNotification>{children}</ErrorNotification>
    </ErrorHandlerContainer>
  );
};

export default ErrorHandler;
